import { useState, type SubmitEvent } from 'react';
import axios, { type AxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, X } from 'lucide-react';
import { config } from '../config';
import { useRegions } from '../hooks/useRegions';
import { useDivisions } from '../hooks/useDivisions';
import { useResourceLimits } from '../hooks/useResourceLimits';
import { useDeviceConnectionMethods } from '../hooks/useDeviceConnectionMethods';
import ActivationPanel from './ActivationPanel';
import DeviceReadinessBanner from './DeviceReadinessBanner';
import type { DeviceReadiness } from '../hooks/useDeviceDetails';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import CredentialSelect from './CredentialSelect';
import {
  PASSWORD_CREDENTIAL_TYPES,
  SNMP_CREDENTIAL_TYPES,
  SSH_CREDENTIAL_TYPES,
} from './credentialTypes';

const NO_SELECTION = '__none__';

interface CreateDeviceRequest {
  name: string;
  ipAddress: string | null;
  regionId: string;
  divisionId?: string | null;
  description?: string | null;
  snmpCredentialId?: string | null;
  sshCredentialId?: string | null;
  httpCredentialId?: string | null;
  useHttps?: boolean;
  deferSnmpPolling?: boolean;
}

interface CreatedDevice {
  id: string;
  readiness?: DeviceReadiness | null;
}

interface ApiErrorResponse {
  error?: string;
  description?: string;
}

/** Pre-fill for activating a device we already know about (discovered). */
export interface DeviceActivatePrefill {
  name?: string | null;
  ipAddress?: string | null;
  regionId?: string | null;
  divisionId?: string | null;
  description?: string | null;
}

interface DeviceActivateFormProps {
  organizationId: string;
  /**
   * Pre-fill values. Absent = the blank "Add device" flow. Present with `deviceId` =
   * "Activate" — we still POST /api/devices (the backend dedups by IP/name+region and
   * updates the existing discovered device in place, keeping its UUID), so activation
   * is genuinely the same operation as creation, just starting from what we know.
   * Present alone = a new device started from what we know of it (an endpoint being
   * promoted): still "Create", device limit included.
   */
  initial?: DeviceActivatePrefill;
  /**
   * The existing device's id, when activating an already-known device from its
   * detail page. Used only to pre-fill the credential selects from the device's
   * current connection methods — the create/activate POST is unchanged.
   */
  deviceId?: string;
  onClose: () => void;
}

/**
 * The single device create/activate form. Both "Add device" and "Activate an
 * existing device" render this: same fields, same POST /api/devices, same
 * connection-method creation, then the same identify→realize `ActivationPanel`.
 * "Activate" only differs by starting pre-filled from the device we discovered.
 */
export default function DeviceActivateForm({ organizationId, initial, deviceId, onClose }: DeviceActivateFormProps) {
  const queryClient = useQueryClient();
  const backendApi = config.backendApi;
  const isActivate = !!initial && !!deviceId;

  const [formName, setFormName] = useState(initial?.name ?? '');
  const [formIpAddress, setFormIpAddress] = useState(initial?.ipAddress ?? '');
  const [formRegionId, setFormRegionId] = useState(initial?.regionId ?? '');
  const [formDivisionId, setFormDivisionId] = useState(initial?.divisionId ?? NO_SELECTION);
  const [formDescription, setFormDescription] = useState(initial?.description ?? '');
  const [formSnmpCredentialId, setFormSnmpCredentialId] = useState(NO_SELECTION);
  const [formSshCredentialId, setFormSshCredentialId] = useState(NO_SELECTION);
  const [formHttpCredentialId, setFormHttpCredentialId] = useState(NO_SELECTION);
  const [formUseHttps, setFormUseHttps] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [activation, setActivation] = useState<{
    deviceId: string;
    mode: 'quick' | 'interactive';
    deviceCredentials: Record<string, string | null>;
    readiness?: DeviceReadiness | null;
  } | null>(null);

  const { data: regions } = useRegions(organizationId);
  const { data: divisions } = useDivisions(organizationId);
  // Existing connection methods for an already-known device — the source of the
  // credential pre-fill. The hook no-ops when deviceId is absent (the Add flow).
  const { data: existingMethods } = useDeviceConnectionMethods(deviceId);
  const { data: resourceLimits } = useResourceLimits();
  const deviceLimits = resourceLimits?.devices;

  const regionsArray = regions ?? [];
  const divisionsArray = divisions ?? [];

  // Default region to the first available if none pre-filled (matches Add flow).
  if (!formRegionId && regions && regions.length > 0) {
    setFormRegionId(regions[0].id);
  }

  // Pre-fill the credential selects from the device's existing connection
  // methods when re-activating (an snmp / ssh / http[s] method with a credential
  // seeds the matching select). Runs once, when the methods first load, and only
  // fills selects the user hasn't already changed — so it never clobbers edits.
  const [didPrefillCreds, setDidPrefillCreds] = useState(false);
  if (!didPrefillCreds && existingMethods) {
    const pick = (types: string[]) => {
      const matches = existingMethods.filter(
        (m) => types.includes(m.sessionType) && m.credentialId,
      );
      return matches.find((m) => m.enabled) ?? matches.at(0);
    };
    const snmpCred = pick(['snmp'])?.credentialId;
    const sshCred = pick(['ssh'])?.credentialId;
    const httpMethod = pick(['http', 'https']);
    if (snmpCred) setFormSnmpCredentialId((cur) => (cur === NO_SELECTION ? snmpCred : cur));
    if (sshCred) setFormSshCredentialId((cur) => (cur === NO_SELECTION ? sshCred : cur));
    if (httpMethod?.credentialId) {
      const httpCred = httpMethod.credentialId;
      setFormHttpCredentialId((cur) => (cur === NO_SELECTION ? httpCred : cur));
      setFormUseHttps(httpMethod.sessionType === 'https');
    }
    setDidPrefillCreds(true);
  }

  const valueOrNull = (v: string) => (v === NO_SELECTION ? null : v);

  const createDeviceMutation = useMutation<CreatedDevice, AxiosError<ApiErrorResponse>, CreateDeviceRequest>({
    mutationFn: async (deviceData) => {
      const response = await axios.post<CreatedDevice>(`${backendApi}/api/devices/`, deviceData, {
        withCredentials: true,
      });
      return response.data;
    },
  });

  // Create/activate the device (the backend dedups + attaches connection methods),
  // then drop into identify → realize in the chosen mode. Same path for both
  // "Add" and "Activate".
  const handleActivate = async (mode: 'quick' | 'interactive') => {
    setSubmitError(null);
    if (!formName || !formRegionId) {
      setSubmitError('Name and Region are required');
      return;
    }
    const httpId = valueOrNull(formHttpCredentialId);
    const deviceCredentials: Record<string, string | null> = {
      snmp: valueOrNull(formSnmpCredentialId),
      ssh: valueOrNull(formSshCredentialId),
      http: httpId,
    };
    const requestData: CreateDeviceRequest = {
      name: formName,
      ipAddress: formIpAddress || null,
      regionId: formRegionId,
      divisionId: valueOrNull(formDivisionId),
      description: formDescription || null,
      snmpCredentialId: deviceCredentials.snmp,
      sshCredentialId: deviceCredentials.ssh,
      httpCredentialId: httpId,
      useHttps: httpId ? formUseHttps : undefined,
      // Don't poll SNMP until realize verifies it, so the device doesn't briefly
      // show "unhealthy" before it's been set up.
      deferSnmpPolling: true,
    };
    try {
      const device = await createDeviceMutation.mutateAsync(requestData);
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
      setActivation({ deviceId: device.id, mode, deviceCredentials, readiness: device.readiness });
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const data = e.response?.data as ApiErrorResponse | undefined;
        setSubmitError(data?.description ?? data?.error ?? 'Failed to start activation');
      } else {
        setSubmitError('Failed to start activation');
      }
    }
  };

  const handleSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    void handleActivate('interactive');
  };

  // Once the device is created/updated, hand off to the realize flow.
  if (activation) {
    return (
      <>
      <DeviceReadinessBanner readiness={activation.readiness} className="mb-4" />
      <ActivationPanel
        deviceId={activation.deviceId}
        organizationId={organizationId}
        regionId={formRegionId}
        mode={activation.mode}
        deviceCredentials={activation.deviceCredentials}
        onClose={() => {
          void queryClient.invalidateQueries({ queryKey: ['devices'] });
          onClose();
        }}
      />
      </>
    );
  }

  // Device-limit gating only applies to genuinely new devices — activating a
  // known device dedups to an update, not a new device.
  const limitBlocked = !isActivate && (deviceLimits?.atLimit ?? false);
  const busy = createDeviceMutation.isPending;

  return (
    <AppSheet
      open
      onOpenChange={(open) => { if (!open) onClose(); }}
      title={isActivate ? 'Activate device' : 'Create new device'}
      size="md"
    >
      <AppSheet.Body>
        {submitError ? (
          <Alert variant="destructive" className="mb-4 relative">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{submitError}</AlertDescription>
            <button
              type="button"
              onClick={() => { setSubmitError(null); }}
              className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </Alert>
        ) : null}
        <form id="device-activate-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="da-name">Name</Label>
            <Input
              id="da-name"
              type="text"
              placeholder="e.g., Router-01"
              value={formName}
              onChange={(e) => { setFormName(e.target.value); }}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="da-ip">IP address or hostname (optional)</Label>
            <Input
              id="da-ip"
              type="text"
              placeholder="e.g., 192.168.1.1 or switch-01.local"
              value={formIpAddress}
              onChange={(e) => { setFormIpAddress(e.target.value); }}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Hostnames will be resolved by the gateway. Leave empty for console-only devices.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="da-region">Region</Label>
            <Select
              value={formRegionId}
              onValueChange={(next) => {
                // Credentials live on their own region's gateways: picks made
                // for another region go with it.
                if (next !== formRegionId) {
                  setFormSnmpCredentialId(NO_SELECTION);
                  setFormSshCredentialId(NO_SELECTION);
                  setFormHttpCredentialId(NO_SELECTION);
                }
                setFormRegionId(next);
              }}
              required
            >
              <SelectTrigger id="da-region">
                <SelectValue placeholder="Select a region" />
              </SelectTrigger>
              <SelectContent>
                {regionsArray.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="da-division">Division (optional)</Label>
            <Select value={formDivisionId} onValueChange={setFormDivisionId}>
              <SelectTrigger id="da-division">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SELECTION}>None</SelectItem>
                {divisionsArray.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="da-snmp">SNMP credential (optional)</Label>
            <CredentialSelect
              id="da-snmp"
              value={valueOrNull(formSnmpCredentialId)}
              onChange={(id) => { setFormSnmpCredentialId(id ?? NO_SELECTION); }}
              regionId={formRegionId}
              divisionId={valueOrNull(formDivisionId)}
              types={SNMP_CREDENTIAL_TYPES}
              optional
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="da-ssh">SSH credential (optional)</Label>
            <CredentialSelect
              id="da-ssh"
              value={valueOrNull(formSshCredentialId)}
              onChange={(id) => { setFormSshCredentialId(id ?? NO_SELECTION); }}
              regionId={formRegionId}
              divisionId={valueOrNull(formDivisionId)}
              types={SSH_CREDENTIAL_TYPES}
              optional
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="da-http">HTTP credential (optional)</Label>
            <CredentialSelect
              id="da-http"
              value={valueOrNull(formHttpCredentialId)}
              onChange={(id) => { setFormHttpCredentialId(id ?? NO_SELECTION); }}
              regionId={formRegionId}
              divisionId={valueOrNull(formDivisionId)}
              types={PASSWORD_CREDENTIAL_TYPES}
              optional
            />
          </div>

          {formHttpCredentialId !== NO_SELECTION ? (
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={formUseHttps}
                  onCheckedChange={(v) => { setFormUseHttps(v === true); }}
                />
                Use HTTPS (port 443)
              </label>
              <p className="text-xs text-muted-foreground">
                {formUseHttps
                  ? 'Will connect on port 443 with HTTPS.'
                  : 'Will connect on port 80 with HTTP.'}
              </p>
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="da-desc">Description (optional)</Label>
            <Textarea
              id="da-desc"
              placeholder="Add a description for this device"
              value={formDescription}
              onChange={(e) => { setFormDescription(e.target.value); }}
            />
          </div>

          {!isActivate && deviceLimits ? (
            <Alert
              className={cn(
                deviceLimits.atLimit
                  ? 'border-sev-1/40 bg-sev-1/10'
                  : deviceLimits.approachingLimit
                    ? 'border-sev-3/40 bg-sev-3/10'
                    : 'border-sev-5/30 bg-sev-5/5',
              )}
            >
              <AlertDescription>
                <strong>Devices:</strong> {deviceLimits.currentCount} of {deviceLimits.softLimit} used
                {deviceLimits.atLimit
                  ? '. You have reached your device limit. Please upgrade your plan or contact support.'
                  : null}
              </AlertDescription>
            </Alert>
          ) : null}
        </form>

      </AppSheet.Body>
      <AppSheet.Footer>
        <Button variant="outline" type="button" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="outline"
          type="button"
          onClick={() => { void handleActivate('quick'); }}
          disabled={busy || limitBlocked}
        >
          Quick activate
        </Button>
        <Button
          type="button"
          onClick={() => { void handleActivate('interactive'); }}
          disabled={busy || limitBlocked}
        >
          {busy ? 'Starting…' : 'Guided setup'}
        </Button>
      </AppSheet.Footer>
    </AppSheet>
  );
}
